import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../config/supabase';
import { LuDollarSign, LuGripVertical } from 'react-icons/lu';
import { format } from 'date-fns';
import OpportunityDetailsModal from './OpportunityDetailsModal';

const OPPORTUNITY_STAGES = [
  { id: 'Lead', label: 'Lead', color: 'bg-gray-100', textColor: 'text-gray-700' },
  { id: 'Qualified', label: 'Qualified', color: 'bg-blue-100', textColor: 'text-blue-700' },
  { id: 'Proposal', label: 'Proposal', color: 'bg-yellow-100', textColor: 'text-yellow-700' },
  { id: 'Negotiation', label: 'Negotiation', color: 'bg-orange-100', textColor: 'text-orange-700' },
  { id: 'Closed Won', label: 'Closed Won', color: 'bg-green-100', textColor: 'text-green-700' },
  { id: 'Closed Lost', label: 'Closed Lost', color: 'bg-red-100', textColor: 'text-red-700' }
];

const PipelineBoard = ({ className = '' }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const dragCounter = useRef(0);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('opportunities')
        .select(`
          *,
          accounts ( id, name, industry ),
          contacts ( id, first_name, last_name, email ),
          users ( id, full_name ),
          teams ( id, name )
        `);

      if (error) throw error;
      setOpportunities(data || []);
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      setError('Failed to load opportunities');
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const handleDragStart = (e, opportunity) => {
    setDraggedItem(opportunity);
    e.dataTransfer.effectAllowed = 'move';
    e.target.classList.add('opacity-50');
  };

  const handleDragEnd = (e) => {
    setDraggedItem(null);
    setDragOverStage(null);
    dragCounter.current = 0;
    e.target.classList.remove('opacity-50');
  };

  const handleDragEnter = (e, stage) => {
    e.preventDefault();
    dragCounter.current++;
    setDragOverStage(stage);
  };

  const handleDragLeave = (e) => {
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverStage(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOverStage(null);

    if (!draggedItem || draggedItem.stage === newStage) {
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Update opportunity stage
      const { error: updateError } = await supabase
        .from('opportunities')
        .update({
          stage: newStage,
          updated_at: new Date().toISOString()
        })
        .eq('id', draggedItem.id);

      if (updateError) throw updateError;

      // Create history record
      const { error: historyError } = await supabase
        .from('opportunity_history')
        .insert({
          opportunity_id: draggedItem.id,
          changed_by_user_id: user.id,
          old_stage: draggedItem.stage,
          new_stage: newStage,
          notes: `Stage changed from ${draggedItem.stage} to ${newStage}`,
          changed_at: new Date().toISOString()
        });

      if (historyError) {
        console.error('Error creating history record:', historyError);
      }

      // Update local state
      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === draggedItem.id
            ? { ...opp, stage: newStage, updated_at: new Date().toISOString() }
            : opp
        )
      );

    } catch (err) {
      console.error('Error updating opportunity:', err);
      setError('Failed to update opportunity stage');
    }
  };

  const getOpportunitiesByStage = (stage) => {
    return opportunities.filter(opp => opp.stage === stage);
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getInitials = (name) => {
    if (!name) return 'NA';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Sales Pipeline</h3>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <span>{opportunities.length} total opportunities</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="flex space-x-4 min-w-max pb-4">
          {OPPORTUNITY_STAGES.map((stage) => {
            const stageOpportunities = getOpportunitiesByStage(stage.id);
            const isDragOver = dragOverStage === stage.id;

            return (
              <div
                key={stage.id}
                className={`flex-1 min-w-72 ${stage.color} rounded-lg p-4 transition-all ${isDragOver ? 'ring-2 ring-blue-400 ring-opacity-50' : ''
                  }`}
                onDragEnter={(e) => handleDragEnter(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className={`font-medium ${stage.textColor}`}>{stage.label}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${stage.textColor} bg-white bg-opacity-50`}>
                    {stageOpportunities.length}
                  </span>
                </div>

                <div className="space-y-3 min-h-96">
                  {stageOpportunities.map((opportunity) => (
                    <div
                      key={opportunity.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, opportunity)}
                      onDragEnd={handleDragEnd}
                      onClick={() => setSelectedOpportunity(opportunity)}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-move hover:shadow-md transition-shadow"
                    >
                      {/* Drag Handle */}
                      <div className="flex items-center justify-between mb-3">
                        <LuGripVertical className="h-4 w-4 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          {opportunity.close_date
                            ? format(new Date(opportunity.close_date), 'MMM d')
                            : 'No close date'
                          }
                        </span>
                      </div>

                      {/* Account Name */}
                      <div className="mb-3">
                        <h5 className="font-medium text-gray-900 line-clamp-1">
                          {opportunity.name}
                        </h5>
                        <p className="text-sm text-gray-600">
                          {opportunity.accounts?.name || 'Unknown Account'}
                        </p>
                      </div>

                      {/* Amount */}
                      <div className="mb-3">
                        <div className="flex items-center space-x-2">
                          <LuDollarSign className="h-4 w-4 text-green-600" />
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(opportunity.amount, opportunity.currency)}
                          </span>
                        </div>
                      </div>

                      {/* Owner Avatar */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          {opportunity.users?.full_name ? (
                            <>
                              <div className="h-6 w-6 bg-blue-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                                {getInitials(opportunity.users.full_name)}
                              </div>
                              <span className="text-xs text-gray-600">
                                {opportunity.users.full_name}
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">Unassigned</span>
                          )}
                        </div>
                        {opportunity.teams?.name && (
                          <span className="text-xs text-gray-500">
                            {opportunity.teams.name}
                          </span>
                        )}
                      </div>

                      {/* Contact */}
                      {opportunity.contacts && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Contact: {opportunity.contacts.first_name} {opportunity.contacts.last_name}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}

                  {stageOpportunities.length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">
                        <div className="h-12 w-12 mx-auto bg-gray-200 rounded-full flex items-center justify-center">
                          <LuGripVertical className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">No opportunities in {stage.label}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <OpportunityDetailsModal
        isOpen={!!selectedOpportunity}
        opportunity={selectedOpportunity}
        onClose={() => setSelectedOpportunity(null)}
        onUpdate={() => {
          fetchOpportunities(true);
        }}
      />
    </div>
  );
};

export default PipelineBoard;